import { type InternalAssetMap } from '@chainflip/utils/chainflip';
import {
  getAllLendingPoolsQuery,
  getAllLpLendingPositionsQuery,
  getAllLpLoanAccountsQuery,
  getLendingGlobalConfigQuery,
} from '@/shared/graphql/cache/lending';
import { getAllLiquidationsQuery, getLendingPoolsApyQuery } from '@/shared/graphql/lp/lending';

export const lendingPoolColors = {
  Sol: '#C9A8FF', // cf-blue-4
  Eth: '#1B76FF', // cf-blue-1
  Btc: '#F4BD8A', // ???
} as Partial<InternalAssetMap<string>>;

/**
 * Convert an LTV (0-50%) to a reversed linear score between 0 and 5.
 * - 0%  -> 5
 * - 50% -> 0
 *
 * Accepts a percentage (i.e 20%)
 */
export function calculateLowLtvPenaltyPercent(
  ltvPercent: number,
  lowLtvPenaltyThresholdPercent: number,
  lowLtvPenaltyPercentAtThreshold: number = 5,
): number {
  if (ltvPercent > lowLtvPenaltyThresholdPercent) return 0;
  const clamped = Math.max(0, Math.min(lowLtvPenaltyThresholdPercent, ltvPercent));
  return (1 - clamped / lowLtvPenaltyThresholdPercent) * lowLtvPenaltyPercentAtThreshold;
}

// TODO(lending): move to product toolkit
export const calculateLoanToValueBps = ({
  totalBorrowAmountUsd,
  totalCollateralAmountUsd,
}: {
  totalBorrowAmountUsd: number;
  totalCollateralAmountUsd: number;
}) => {
  if (totalCollateralAmountUsd <= 0 && totalBorrowAmountUsd > 0) return 10_000; // 100% LTV
  if (totalCollateralAmountUsd === 0) return 0;
  return Math.min((totalBorrowAmountUsd / totalCollateralAmountUsd) * 10_000, 10_000);
};

export const DEFAULT_APY_PERIOD = { value: '168H', label: '7D', description: '7 days' } as const;

export const APY_AVERAGE_PERIODS = [
  { value: '6H', label: '6H', description: '6 hours' },
  { value: '24H', label: '24H', description: '24 hours' },
  { value: '72H', label: '3D', description: '3 days' },
  DEFAULT_APY_PERIOD,
  { value: '720H', label: '30D', description: '30 days' },
] as const;

export type ApyAveragePeriodOption = (typeof APY_AVERAGE_PERIODS)[number];

export type ApyAveragePeriod = ApyAveragePeriodOption['value'];

export type ApyByPeriod = Record<ApyAveragePeriod, number>;

export const EMPTY_APY_BY_PERIOD: ApyByPeriod = {
  '6H': 0,
  '24H': 0,
  '72H': 0,
  '168H': 0,
  '720H': 0,
};

export const CURRENT_BORROW_APY_PERIOD = { value: 'CURRENT', label: 'Current' } as const;

export const BORROW_APY_PERIODS = [CURRENT_BORROW_APY_PERIOD, ...APY_AVERAGE_PERIODS] as const;

export type BorrowApyPeriodOption = (typeof BORROW_APY_PERIODS)[number];

export type BorrowApyPeriod = BorrowApyPeriodOption['value'];

export const SUPPLY_APY_PERIODS = APY_AVERAGE_PERIODS;

// lending query consts
export const LP_LOAN_ACCOUNTS_QUERY = getAllLpLoanAccountsQuery;
export const LIQUIDATIONS_QUERY = getAllLiquidationsQuery;
export const LP_LENDING_POSITIONS_QUERY = getAllLpLendingPositionsQuery;
export const LENDING_POOLS_QUERY = getAllLendingPoolsQuery;
export const LENDING_CONFIG_QUERY = getLendingGlobalConfigQuery;
export const LENDING_POOLS_APY_QUERY = getLendingPoolsApyQuery;
